import { useState, useEffect } from "react";
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthenticator, Heading, Text, View, Flex, Button } from "@aws-amplify/ui-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { s3ReadJsonArray, s3ListObjects, s3AppendJsonArray } from '../components/s3Utils';
import { verifiedUser } from "../components/utils";
import questions from "../components/screening/questions";
import QuestionTemplate from "../components/screening/questionTemplate";
import QuestionDate from "../components/screening/questionDate";

const Screening = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const navigate = useNavigate();
    const filename = "Wuerzburger_Screening.json";
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const handleS3File = (result) => {
            const keys = Object.keys(result);
            if (keys.includes(filename)) {
                s3ReadJsonArray(filename, 'private', (json) => {
                    if (isMounted) {
                        setValues(json);
                    }
                });
            }
        }
        const setValues = (json) => {
            try {
                if (json.length > 0) {
                    const lastEntry = json[json.length - 1];
                    if (lastEntry.hasOwnProperty("CLOSED") && lastEntry["CLOSED"]) {
                        setDisabled(true);
                    }
                    if (lastEntry.hasOwnProperty("WUERZBURGER_SCREENING")) {
                        setAnswers(lastEntry["WUERZBURGER_SCREENING"]);
                    }
                }
            } catch (err) {
                console.error("Fehler beim Parsen der JSON-Daten:", err);
            }
        }
        s3ListObjects('', 'private', handleS3File);
        return () => {
            isMounted = false;
        };
    }, []);

    if (!verifiedUser(user)) {
        return <Navigate to="/profile" replace />;
    }

    const shouldShowDateQuestion = () => {
        const q = answers["question1"];
        return q === "6" || q === "7";
    };

    const hasDateQuestion = shouldShowDateQuestion();
    const totalSteps = questions.length + (hasDateQuestion ? 1 : 0);

    const getCurrentQuestion = () => {
        if (hasDateQuestion) {
            if (step === 0) return questions[0];
            if (step === 1) return questions[1];
            if (step === 2) return { "id": "question1_date" };
            return questions[step - 1];
        } else {
            return questions[step];
        }
    };

    const current = getCurrentQuestion();

    const getHeadingNumber = () => {
        return `${step + 1}. Frage`;
    };

    const getDateFollowupText = () => {
        const answer = answers["question1"];
        const q1 = questions.find(q => q.id === "question1");
        const option = q1?.options.find(opt => opt.id === answer);
        return option?.dateFollowupText || "Bitte geben Sie ein Datum an.";
    };

    const handleChange = (value) => {
        const key = current.id;
        setAnswers((prev) => ({ ...prev, [key]: value }));
    };

    const handleNext = (e) => {
        saveAnswers();
        if (step < totalSteps - 1) {
            setStep((prev) => prev + 1);
        } else {
            navigate("/");
        }
        e.currentTarget.blur();
    };

    const handleBack = (e) => {
        saveAnswers();
        if (step > 0) {
            setStep((prev) => prev - 1);
        }
        e.currentTarget.blur();
    };

    const saveAnswers = () => {
        if (!disabled) {
            s3AppendJsonArray(filename, { CHANGED: new Date().toISOString(), WUERZBURGER_SCREENING: answers, USER: user.username, MASSNID: user.attributes['custom:kvsID'] }, "private");
        }
    };

    return (
        <View padding="2rem">
            <Flex direction="column" alignItems="center" gap="1.5rem">
                <Heading style={{
                    color: '#88a6c7',
                    fontSize: '3rem',
                    fontWeight: 600
                }}>Würzburger Screening</Heading>
                <Text textAlign="center" maxWidth="600px">
                    Bitte beantworten Sie die folgenden Fragen nach bestem Wissen und Gewissen.
                </Text>

                {current.id === "question1_date" ? (
                    <QuestionDate
                        heading={getHeadingNumber()}
                        question={getDateFollowupText()}
                        value={answers[current.id] || ""}
                        onChange={handleChange}
                        disabled={disabled}
                    />
                ) : (
                    <QuestionTemplate
                        heading={getHeadingNumber()}
                        question={current.question}
                        options={current.options}
                        value={answers[current.id] || ""}
                        onChange={handleChange}
                        disabled={disabled}
                    />
                )}
                <Flex direction="row" justifyContent="space-between" width="100%" maxWidth="800px">
                    <Button variation="primary" onClick={handleBack} isDisabled={step === 0}>
                        <ChevronLeft /> Zurück
                    </Button>
                    <Button variation="primary" onClick={handleNext}>
                        {step < totalSteps - 1 ? <>Weiter <ChevronRight /></> : "Abschließen"}
                    </Button>
                </Flex>
            </Flex>
        </View>
    );
};

export default Screening;
